import { inject, Injectable } from '@angular/core';
import { map, Observable, of, throwError } from 'rxjs';
import { HttpClient, HttpContext, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { SKIP_AUTH_ERROR_HANDLER } from '../interceptors/skip-auth-error.tokens';
import { GlobalService } from 'src/app/shared/ui/services/global.service';
import { GlobalStateService } from '../state/global-state.service';
import { CompaniesService } from './companies.service';
import { AuthService } from './authentication.service';
import { Constants } from '../constants/constants';

@Injectable({
    providedIn: 'root'
})
export class OAuthAuthenticationService {
    // Section: SERVICES
    //----------------------------------------------------------------------
    private httpClient: HttpClient = inject(HttpClient);
    private globalService = inject(GlobalService);
    private globalState = inject(GlobalStateService);
    private companiesService = inject(CompaniesService);
    private authService = inject(AuthService);

    // Section: PRIVATE PROPERTIES
    //----------------------------------------------------------------------
    private redirectUri: string = this.globalService.backofficeUrl + 'oauth-success';

    // Section: PUBLIC METHODS
    //----------------------------------------------------------------------

    authorize(loginConfiguration: any): void {
        const url = this.getAuthorizeUrl(loginConfiguration);
        window.open(url, '_self');
    }

    getLoginConfiguration(companyBrand?: string): Observable<any> {
        const loginConfiguration = sessionStorage.getItem('loginConfiguration');
        if (loginConfiguration) {
            return of(JSON.parse(loginConfiguration));
        }

        const company = companyBrand || localStorage.getItem('lastUserLoginBrand');
        const ssoConfigId = localStorage.getItem('ssoConfigId');
        if (!company || !ssoConfigId) {
            return throwError(() => new Error());
        }

        return this.companiesService.getCompanyLoginConfiguration(company).pipe(
            map((response: any) => {
                if (!response.loginConfiguration) {
                    throw new Error();
                }
                const ssoConfigs: any[] = response.loginConfiguration.ssoConfigs || [];
                const selectedConfig = ssoConfigs.find((cfg: any) => cfg.id == ssoConfigId) || ssoConfigs[0];

                if (!selectedConfig) {
                    throw new Error();
                }

                localStorage.removeItem('ssoConfigId');
                sessionStorage.setItem('loginConfiguration', JSON.stringify(selectedConfig));
                return selectedConfig;
            })
        );
    }

    authenticate(authCode: string, company: string): void {
        this.getLoginConfiguration(company).subscribe({
            next: (loginConfig) => {
                this.authenticateUser(authCode, company, loginConfig);
            },
            error: (err) => {
                this.handleOAuthAuthenticationError(err);
                // console.error(err);
                // clearUserSessionAndRedirect();
                // $rootScope.externalLoginFailed = true;
            }
        });
    }

    // Section: PRIVATE METHODS
    //----------------------------------------------------------------------
    private uuidv4() {
        return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) => {
            const r = crypto.getRandomValues(new Uint8Array(1))[0];
            const v = Number(c) ^ (r & (15 >> (Number(c) / 4)));
            return v.toString(16);
        });
    }

    private getClientId(clientId: string): string | undefined {
        if (!clientId) {
            return;
        }
        return clientId.split(',')[0];
    }

    private parseExtraParams(extraParams: any): string {
        if (!extraParams) {
            return '';
        }
        const appVersion = '7.2.0';
        if (extraParams.indexOf('APP_VERSION') !== -1) {
            extraParams = extraParams.replace('APP_VERSION', appVersion);
        }
        if (extraParams.indexOf('ID_SO') !== -1) {
            extraParams = extraParams.replace('ID_SO', 2);
        }
        return '&' + extraParams;
    }

    private getAuthorizeUrl(loginConfiguration: any): string {
        let url = loginConfiguration.loginURL.replace(/\/$/, '');
        const scope = loginConfiguration.scope || 'openid+user.read+profile';
        const nonce = this.uuidv4();
        const clientId = this.getClientId(loginConfiguration.clientId);
        const responseType = loginConfiguration.responseType ? loginConfiguration.responseType : 'id_token';

        url += '?client_id=' + clientId + '&response_type=' + responseType + '&redirect_uri=' + this.redirectUri + '&response_mode=fragment&scope=' + scope;
        if (loginConfiguration.state) {
            // TODO: generate random string, save in session for later validation
            url += '&state=' + loginConfiguration.state;
        }
        if (loginConfiguration.nonce) {
            url += '&nonce=' + loginConfiguration.nonce;
        } else {
            url += '&nonce=' + nonce;
        }
        url += this.parseExtraParams(loginConfiguration.extraParams);
        console.log(url);
        return url;
    }

    private parseJwt(token: string) {
        var base64Url = token.split('.')[1];
        var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        var jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                })
                .join('')
        );
        return JSON.parse(jsonPayload);
    }

    private getUsernameFromToken(token: string, usernameClaim: string, regex: any, usernameGroup: string) {
        var payload = this.parseJwt(token);
        if (!payload || !usernameClaim || !payload[usernameClaim]) {
            return false;
        }
        if (regex && usernameGroup) {
            var username = payload[usernameClaim].match(regex);
            return !!username && username.length > usernameGroup ? username[usernameGroup] : payload[usernameClaim];
        } else {
            return payload[usernameClaim];
        }
    }

    private authenticateUserInternally(company: string, idToken: string, username: string, clientId: string): Observable<HttpResponse<any>> {
        const url = `${Constants.apiUrl}/users/oauth2-authentication`;
        const effectiveClientId = this.getClientId(clientId);

        const body = new HttpParams()
            .set('c', company)
            .set('jwtToken', idToken)
            .set('username', username)
            .set('clientId', effectiveClientId || '');

        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
        });

        const httpContext = new HttpContext().set(SKIP_AUTH_ERROR_HANDLER, true);

        return this.httpClient.post<any>(url, body.toString(), {
            headers,
            observe: 'response',
            context: httpContext
        });
    }

    private handleOAuthAuthenticationSuccess(response: HttpResponse<any>): void {
        const authorizationBearer = response.headers.get('Authorization');
        this.authService.saveAuthorizationToken(authorizationBearer);

        const user = response.body;
        this.authService.setUserSession(user);
        this.authService.setUserLanguage();

        this.globalService.redirectUserAfterLogin();
    }

    private handleOAuthAuthenticationError(error: any): void {
        console.error('OAuth Authentication Error:', error);

        // TODO: if error.status === 401 -> show message to user
        // clearUserSessionAndRedirect();
        this.globalState.setExternalLoginFailed(true);
    }

    private authenticateUser(idToken: string, company: string, loginConfig: any) {
        var username = this.getUsernameFromToken(idToken, loginConfig.usernameClaim, loginConfig.regex, loginConfig.usernameGroup);
        if (username) {
            localStorage.setItem('lastUserLoginBrand', company);

            this.authenticateUserInternally(company, idToken, username, loginConfig.clientId).subscribe({
                next: (response) => this.handleOAuthAuthenticationSuccess(response),
                error: (err) => this.handleOAuthAuthenticationError(err)
            });
        } else {
            console.error('Could not extract username from token for OAuth.');
        }
    }
}
